import React from "react";
import {
  FrequentlyBoughtTogether,
  LookingSimilar,
  RelatedProducts,
  TrendingItems,
} from "@algolia/recommend-react";
import recommend, { RecommendClient } from "@algolia/recommend";
import { RecommendView } from "./components/RecommendedView";
import RelatedItem from "./components/RelatedItem";
import { AlgoliaRecommendProps } from "./components/types/AlgoliaRecommendProps";
import { RecommendationType } from "./components/types/RecommendationType";

function HeaderComponent(title: string) {
  return () => (title ? <h4>{title}</h4> : null);
}

const ParseQueryParametersFromText = (queryParametersText: string) => {
  try {
    if (queryParametersText) {
      return JSON.parse(queryParametersText);
    }
  } catch (error) {}

  return undefined;
};

const transformIntoQueryString = (query: any) => {
  return Object.keys(query)
    .sort()
    .filter((key) => query[key] !== undefined)
    .map((key) => {
      return encodeURIComponent(key) + "=" + encodeURIComponent(typeof query[key] == "object" ? JSON.stringify(query[key]) : query[key]);
    })
    .join("&");
};

const performProxyCall = async (model: string, query: string) => {
  const response = await fetch(`/api/algolia/proxy/${model}?${query}`);

  if (response.status >= 400) {
    throw new Error("proxy response status was 400 or above, status: " + response.status);
  }

  const result = await response.json();
  return result;
}

const recommendProxyClient = (base: RecommendClient, apiKey) => {
  return {
    ...base,
    async getTrendingItems(query, o) {
      try {
        return await performProxyCall("trending-items", transformIntoQueryString({ ...query[0], appId: base.appId, apiKey }));
      } catch (error) {
        console.error("error while doing proxy request", error);
        return { results: [{ hits: [] }] };
      }
    },
    async getRelatedProducts(query, o) {
      try {
        return await performProxyCall("related-products", transformIntoQueryString({ ...query[0], appId: base.appId, apiKey }));
      } catch (error) {
        console.error("error while doing proxy request", error);
        return { results: [{ hits: [] }] };
      }
    },
    async getFrequentlyBoughtTogether(query, o) {
      try {
        return await performProxyCall("bought-together", transformIntoQueryString({ ...query[0], appId: base.appId, apiKey })); 
      } catch (error) {
        console.error("error while doing proxy request", error);
        return { results: [{ hits: [] }] };
      }
    },
    async getLookingSimilar(query, o) {
      try {
        return await performProxyCall("looking-similar", transformIntoQueryString({ ...query[0], appId: base.appId, apiKey }));
      } catch (error) {
        console.error("error while doing proxy request", error);
        return { results: [{ hits: [] }] };
      }
    },
  } as RecommendClient;
};

const AlgoliaRecommend = ({
  appId,
  apiKey,
  indexName,
  objectIds,
  title,
  recommendationType,
  maxRecommendations,
  facetName = undefined,
  facetValue = undefined,
  queryParametersText = undefined,
  showBuyButton = false,
  slidesPerView = undefined,
  fallbackParameters = undefined,
}: AlgoliaRecommendProps) => {

  const recommendClient = recommendProxyClient(
    recommend(appId, apiKey),
    apiKey
  );
  const currentObjectIds = objectIds ? objectIds.split(",") : [];
  const queryParameters = ParseQueryParametersFromText(queryParametersText);

  let AlgoliaComponent;
  switch (recommendationType) {
    case RecommendationType.FrequentlyBoughtTogether:
      AlgoliaComponent = FrequentlyBoughtTogether;
      break;
    case RecommendationType.RelatedProducts:
      AlgoliaComponent = RelatedProducts;
      break;
    case RecommendationType.TrendingItems:
      AlgoliaComponent = TrendingItems;
      break;
    case RecommendationType.LookingSimilar:
      AlgoliaComponent = LookingSimilar;
      break;
    default:
      AlgoliaComponent = null;
      break;
  }

  const RelatedItemWrapper = (props) => (
    <RelatedItem {...props} showBuyButton={showBuyButton} />
  );
  
  const RecommendViewWrapper = (props) => (
    <RecommendView {...props} slidesPerView={slidesPerView} showBuyButton={showBuyButton} />
  );

  return (
    <>
      {AlgoliaComponent && (
        <AlgoliaComponent
          recommendClient={recommendClient}
          indexName={indexName}
          objectIDs={currentObjectIds}
          itemComponent={RelatedItemWrapper}
          view={RecommendViewWrapper}
          maxRecommendations={maxRecommendations}
          headerComponent={HeaderComponent(title)}
          facetName={facetName}
          facetValue={facetValue}
          queryParameters={queryParameters}
          fallbackParameters={fallbackParameters}
        />
      )}
    </>
  );
};

export default AlgoliaRecommend;
